import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { fmtMoney, fmtDateTime } from "../lib/format";
import { CheckCircle2, Star, TrendingDown, AlertCircle, Loader2, X } from "lucide-react";

export default function Billing() {
  const [plans, setPlans] = useState({});
  const [me, setMe] = useState(null);
  const [packs, setPacks] = useState({});
  const [billingCycle, setBillingCycle] = useState("monthly");
  const [busy, setBusy] = useState("");
  const [banner, setBanner] = useState(null); // {kind: success|error|pending, text}
  const [params, setParams] = useSearchParams();

  const load = useCallback(async () => {
    const [p, m, c] = await Promise.all([
      api.get("/billing/plans"),
      api.get("/billing/me"),
      api.get("/billing/credit-packs"),
    ]);
    setPlans(p.data); setMe(m.data); setPacks(c.data);
  }, []);

  // Poll status when returning from Stripe
  const pollStatus = useCallback(async (sessionId, attempts = 0) => {
    if (attempts >= 8) {
      setBanner({ kind: "pending", text: "Payment received. Waiting for Stripe to finish activating your account." });
      return;
    }
    try {
      const r = await api.get(`/billing/checkout/status/${sessionId}`);
      if (r.data.status === "settled") {
        setBanner({ kind: "success", text: r.data.kind === "credits" ? "Credits added!" : "Subscription activated!" });
        await load();
        return;
      }
      if (r.data.status === "expired") {
        setBanner({ kind: "error", text: "Payment session expired." });
        return;
      }
      setBanner({ kind: "pending", text: "Confirming payment…" });
      setTimeout(() => pollStatus(sessionId, attempts + 1), 1800);
    } catch (e) {
      setBanner({ kind: "error", text: "Failed to verify payment." });
    }
  }, [load]);

  useEffect(() => {
    load();
    const sid = params.get("session_id");
    const cancelled = params.get("cancelled");
    if (sid) {
      setBanner({ kind: "pending", text: "Confirming payment…" });
      pollStatus(sid);
      setParams({});
    } else if (cancelled) {
      setBanner({ kind: "error", text: "Checkout cancelled." });
      setParams({});
    }
    // eslint-disable-next-line
  }, []);

  const subscribe = async (plan) => {
    setBusy(`plan-${plan}`);
    try {
      const r = await api.post("/billing/checkout", {
        plan, cycle: billingCycle,
      });
      window.location.href = r.data.url;
    } catch (e) {
      setBanner({ kind: "error", text: e.response?.data?.detail || "Checkout failed" });
      setBusy("");
    }
  };

  const buyPack = async (pack) => {
    setBusy(`pack-${pack}`);
    try {
      const r = await api.post("/billing/buy-credits", { pack });
      window.location.href = r.data.url;
    } catch (e) {
      setBanner({ kind: "error", text: e.response?.data?.detail || "Checkout failed" });
      setBusy("");
    }
  };

  const manageSubscription = async () => {
    setBusy("portal");
    try {
      const r = await api.post("/billing/portal");
      window.location.href = r.data.url;
    } catch (e) {
      setBanner({ kind: "error", text: e.response?.data?.detail || "Could not open billing portal" });
      setBusy("");
    }
  };

  if (!me) return <div className="text-slate-500">Loading…</div>;
  const currentPlan = me.plan || "payg";
  const isAnnual = billingCycle === "annual";
  const isTrial = me.status === "trialing";
  const trialDaysLeft = isTrial && me.trial_ends_at
    ? Math.max(0, Math.ceil((new Date(me.trial_ends_at) - Date.now()) / 86400000))
    : 0;
  const credits = me.load_credits ?? 0;
  const audience = me.audience || "broker";
  const audienceLabel = { driver: "drivers", broker: "brokers & carriers", shipper: "shippers" }[audience];
  const audiencePlanKeys = Object.keys(plans).filter((k) => plans[k].audience === audience);
  const renewalDate = me.subscription_current_period_end || me.subscription_end_date;
  const canBuyCredits = audience === "shipper";

  return (
    <div className="space-y-8">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-[11px] tracking-[0.2em] uppercase text-gold font-bold mb-2">Billing</div>
          <h1 className="font-display text-4xl font-bold tracking-tighter text-navy">Pricing that beats the market.</h1>
          <p className="text-slate-500 mt-2 max-w-2xl">
            Compare to DAT ($45–$199), Truckstop ($39–$149), 123Loadboard ($35–$55), Trucker Path ($25–$45).
            We undercut every tier and bundle the driver app, weather alerts, and document storage.
          </p>
        </div>
        <div className="inline-flex bg-white border border-slate-200 rounded-full p-1" data-testid="billing-cycle-toggle">
          <button onClick={() => setBillingCycle("monthly")} className={`px-4 py-1.5 rounded-full text-xs font-bold tracking-wider uppercase ${billingCycle === "monthly" ? "bg-navy text-white" : "text-slate-500"}`} data-testid="cycle-monthly">Monthly</button>
          <button onClick={() => setBillingCycle("annual")} className={`px-4 py-1.5 rounded-full text-xs font-bold tracking-wider uppercase flex items-center gap-1 ${billingCycle === "annual" ? "bg-navy text-white" : "text-slate-500"}`} data-testid="cycle-annual">
            Annual <span className="bg-gold text-navy text-[9px] px-1.5 py-0.5 rounded-full">2 mo free</span>
          </button>
        </div>
      </div>

      {banner && (
        <div className={`card-base p-4 flex items-start gap-3 ${banner.kind === "success" ? "border-emerald-300 bg-emerald-50" : banner.kind === "error" ? "border-red-300 bg-red-50" : "border-amber-300 bg-amber-50"}`} data-testid="billing-banner">
          {banner.kind === "success" ? <CheckCircle2 className="text-emerald-600 flex-shrink-0" />
           : banner.kind === "error" ? <AlertCircle className="text-red-600 flex-shrink-0" />
           : <Loader2 className="text-amber-600 flex-shrink-0 animate-spin" />}
          <div className="flex-1 text-sm font-semibold text-slate-800">{banner.text}</div>
          <button onClick={() => setBanner(null)} className="text-slate-500 hover:text-slate-700" data-testid="banner-close"><X size={16} /></button>
        </div>
      )}

      {/* Current state summary */}
      <div className="grid md:grid-cols-3 gap-4">
        <div className="card-base p-5">
          <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500 mb-1">Current plan</div>
          <div className="font-display text-2xl font-bold text-navy uppercase">{plans[currentPlan]?.name || currentPlan}</div>
          <div className="text-xs text-slate-500 mt-1">
            {isTrial ? <span className="text-amber-700 font-semibold">Trial · {trialDaysLeft} days left</span>
             : me.status === "active" ? <span className="text-emerald-700 font-semibold">Active</span>
             : me.status}
          </div>
        </div>
        <div className="card-base p-5">
          <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500 mb-1">Load credits</div>
          <div className="font-display text-2xl font-bold text-navy">{credits}</div>
          <div className="text-xs text-slate-500 mt-1">{currentPlan === "payg" ? "Used per load post on PAYG" : "Reserve for PAYG fallback"}</div>
        </div>
        <div className="card-base p-5">
          <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500 mb-1">Renews</div>
          <div className="font-display text-2xl font-bold text-navy">
            {renewalDate ? new Date(renewalDate).toLocaleDateString() : "—"}
          </div>
          {currentPlan !== "payg" && !isTrial && (
            <button onClick={manageSubscription} disabled={busy === "portal"} className="text-xs text-navy font-semibold hover:underline mt-1" data-testid="manage-subscription">
              {busy === "portal" ? "Opening portal…" : "Manage subscription"}
            </button>
          )}
          {me.cancel_at_period_end && <div className="text-xs text-amber-700 font-semibold mt-1">Cancels at period end</div>}
        </div>
      </div>

      {/* PAYG card + credit packs */}
      <div className="card-base p-6" data-testid="plan-card-payg">
        <div className={`grid gap-6 items-start ${canBuyCredits ? "md:grid-cols-2" : ""}`}>
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-gold mb-2">No commitment</div>
            <div className="font-display text-2xl font-bold text-navy">Pay-as-you-go</div>
            <div className="font-display text-5xl font-bold tracking-tighter text-navy mt-3">$0.99<span className="text-base text-slate-500 font-normal">/load</span></div>
            <ul className="space-y-2 mt-5">
              {(plans.payg?.features || []).map((f, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                  <CheckCircle2 size={16} className="text-emerald-500 flex-shrink-0 mt-0.5" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
          </div>
          {canBuyCredits && (
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500 mb-3">Buy load credits</div>
              <div className="space-y-2">
                {Object.entries(packs).map(([key, p]) => (
                  <button
                    key={key}
                    onClick={() => buyPack(key)}
                    disabled={busy === `pack-${key}`}
                    className="w-full flex items-center justify-between p-4 border border-slate-200 rounded-md hover:border-gold hover:bg-amber-50/30 transition"
                    data-testid={`buy-${key}`}
                  >
                    <div className="text-left">
                      <div className="font-display font-bold text-navy">{p.name}</div>
                      <div className="text-xs text-slate-500">${(p.price / p.credits).toFixed(2)} per load</div>
                    </div>
                    <div className="font-display font-bold text-navy">${p.price.toFixed(2)}</div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Subscription plans */}
      <div>
        <div className="font-display text-xl font-bold text-navy mb-4">Plans for {audienceLabel}</div>
        <div className="grid md:grid-cols-3 gap-5">
          {audiencePlanKeys.map((key) => {
            const p = plans[key]; if (!p) return null;
            const isCurrent = key === currentPlan;
            const isPro = !!p.popular;
            const monthly = p.price;
            const annual = p.annual_price ?? monthly * 10;
            const displayPrice = isAnnual ? Math.round(annual / 12) : monthly;
            return (
              <div key={key} className={`card-base p-7 relative ${isPro ? "border-gold shadow-xl scale-[1.02]" : ""}`} data-testid={`plan-card-${key}`}>
                {isPro && <div className="absolute -top-3 left-7 bg-gold text-navy text-[10px] uppercase tracking-[0.2em] font-bold px-2.5 py-1 rounded">Most popular</div>}
                <div className="font-display text-2xl font-bold text-navy">{p.name}</div>
                {p.tagline && (
                  <div className="flex items-start gap-1.5 mt-1.5 text-[11px] font-semibold text-emerald-700">
                    <TrendingDown size={12} className="flex-shrink-0 mt-0.5" />
                    <span>{p.tagline}</span>
                  </div>
                )}
                <div className="my-5 flex items-baseline gap-1">
                  <span className="font-display text-5xl font-bold tracking-tighter text-navy">${displayPrice}</span>
                  <span className="text-slate-500 text-sm">/mo</span>
                </div>
                {isAnnual && <div className="text-xs text-emerald-700 font-semibold mb-2">${annual}/yr · save ${monthly * 12 - annual}</div>}
                <div className="text-sm text-slate-600 mb-5 pb-5 border-b border-slate-100">
                  {p.loads_per_month === -1 ? "Unlimited loads" : `${p.loads_per_month} load posts / month`}
                </div>
                <ul className="space-y-2.5 mb-7">
                  {p.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                      <CheckCircle2 size={16} className="text-emerald-500 flex-shrink-0 mt-0.5" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <button
                  disabled={isCurrent || busy === `plan-${key}`}
                  onClick={() => subscribe(key)}
                  className={`w-full ${isPro ? "btn-primary" : "btn-secondary"} disabled:opacity-50`}
                  data-testid={`subscribe-${key}`}
                >
                  {isCurrent ? <><Star size={14} /> Current plan</> : busy === `plan-${key}` ? "Redirecting…" : `Get ${p.name}`}
                </button>
              </div>
            );
          })}
        </div>
      </div>

      {/* Competitor comparison */}
      <div className="card-base p-6">
        <div className="font-display text-lg font-bold text-navy mb-4">How we compare (Jan 2026)</div>
        <div className="overflow-x-auto">
          <table className="table-base">
            <thead><tr><th>Platform</th><th>Entry</th><th>Mid</th><th>Top</th><th>Pay-as-you-go</th><th>Driver app</th><th>Weather alerts</th></tr></thead>
            <tbody>
              <tr className="bg-gold/5">
                <td className="font-display font-bold text-navy">SpeedyShip ⚡</td>
                <td className="font-mono font-bold text-emerald-700">$19</td>
                <td className="font-mono font-bold text-emerald-700">$59</td>
                <td className="font-mono font-bold text-emerald-700">$149</td>
                <td className="font-mono font-bold text-emerald-700">$0.99/load</td>
                <td><span className="badge accepted">Included</span></td>
                <td><span className="badge accepted">Included</span></td>
              </tr>
              <tr><td>DAT One</td><td className="font-mono">$45</td><td className="font-mono">$99</td><td className="font-mono">$199</td><td className="font-mono">—</td><td><span className="badge draft">Add-on</span></td><td><span className="badge draft">No</span></td></tr>
              <tr><td>Truckstop</td><td className="font-mono">$39</td><td className="font-mono">$99</td><td className="font-mono">$149</td><td className="font-mono">—</td><td><span className="badge accepted">Yes</span></td><td><span className="badge draft">Limited</span></td></tr>
              <tr><td>123Loadboard</td><td className="font-mono">$35</td><td className="font-mono">$45</td><td className="font-mono">$55</td><td className="font-mono">—</td><td><span className="badge accepted">Yes</span></td><td><span className="badge draft">No</span></td></tr>
              <tr><td>Trucker Path</td><td className="font-mono">$25</td><td className="font-mono">$35</td><td className="font-mono">$45</td><td className="font-mono">—</td><td><span className="badge accepted">Yes</span></td><td><span className="badge draft">No</span></td></tr>
            </tbody>
          </table>
        </div>
        <div className="text-xs text-slate-500 mt-3">SpeedyShip is the only major load board with a true zero-monthly pay-as-you-go tier.</div>
      </div>

      <div className="card-base">
        <div className="px-6 py-4 border-b border-slate-200 font-display text-lg font-bold text-navy">Invoices</div>
        <div className="overflow-x-auto">
          <table className="table-base">
            <thead><tr><th>Invoice</th><th>Plan</th><th>Amount</th><th>Status</th><th>Date</th></tr></thead>
            <tbody>
              {me.invoices.length === 0 && <tr><td colSpan={5} className="text-center py-8 text-slate-400">No invoices yet.</td></tr>}
              {me.invoices.map(i => (
                <tr key={i.invoice_id} data-testid={`invoice-${i.invoice_id}`}>
                  <td className="font-mono text-xs">{i.invoice_id}</td>
                  <td className="capitalize font-semibold">{i.plan}{i.cycle && i.cycle !== "monthly" ? ` · ${i.cycle}` : ""}</td>
                  <td className="font-mono">{fmtMoney(i.amount)}</td>
                  <td><span className="badge accepted">{i.status}</span></td>
                  <td className="text-xs text-slate-500">{fmtDateTime(i.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
